import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";

export default function CategorySection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <section id="categories" className="bg-background-100">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">
            {t("categories.title")}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
            {t("categories.subtitle")}
          </p>
        </div>

        <div ref={ref} className="relative mx-auto mt-8 w-full max-w-md">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex w-full items-center justify-between rounded-full border border-background-200 bg-white px-5 py-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-50"
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span className="flex items-center gap-2">
              <i className="ri-grid-line text-primary-500"></i>
              {t("categories.select")}
            </span>
            <i className={`${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-foreground-500`}></i>
          </button>

          {open && (
            <ul
              role="listbox"
              className="absolute left-0 right-0 z-30 mt-2 max-h-80 overflow-y-auto rounded-2xl border border-background-200 bg-white py-2"
            >
              {categories.map((cat) => (
                <li key={cat.id}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-between px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                    role="option"
                  >
                    <span className="flex items-center gap-3">
                      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-background-100">
                        <i className="ri-arrow-right-up-line text-xs text-primary-500"></i>
                      </span>
                      {t(`categoryNames.${cat.id}`)}
                    </span>
                    <i className="ri-external-link-line text-xs text-foreground-400"></i>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {categories.map((cat) => (
            <a
              key={cat.id}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group overflow-hidden rounded-xl border border-background-200 bg-white transition-transform hover:-translate-y-1"
              data-product-shop
            >
              <div className="h-36 w-full overflow-hidden sm:h-40">
                <img
                  src={cat.image}
                  alt={t(`categoryNames.${cat.id}`)}
                  title={`${t("brand")} ${t(`categoryNames.${cat.id}`)}`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex items-center justify-between px-3 py-3">
                <span className="text-sm font-semibold text-foreground-900">
                  {t(`categoryNames.${cat.id}`)}
                </span>
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-50 text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-white">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}